import {
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
  Option,
  Select,
  Stack,
  Typography,
  styled,
} from '@healthinal/ui';
import isEmail from 'is-email';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { UpdateAddressContactDto } from '../../../api/created/addressContact.ts';
import { useCheckAddressContactDuplicate } from '../../../api/created/addressContactDuplicate.ts';
import { useGetAddresses } from '../../../api/generated/address-controller/address-controller.ts';
import { AddressContactDto, AddressDto } from '../../../api/generated/model';
import { FormActionButtons } from '../../../components/form/FormActionButtons.tsx';
import { isPhoneNumberValid } from '../../../utils/phoneNumber.ts';
import { SelectAddressMulti } from './SelectAddressMulti.tsx';

interface FormValues {
  addresses: AddressDto[];
  salutation: string;
  firstName: string;
  lastName: string;
  function: string;
  phone: string;
  email: string;
}

const EMPTY_FORM_VALUES: FormValues = {
  addresses: [],
  salutation: '',
  firstName: '',
  lastName: '',
  function: '',
  phone: '',
  email: '',
};

function contactToFormValues(contact: AddressContactDto, addresses: AddressDto[]): FormValues {
  return {
    addresses,
    salutation: contact.salutation ?? '',
    firstName: contact.firstName ?? '',
    lastName: contact.lastName ?? '',
    function: contact.function ?? '',
    phone: contact.phone ?? '',
    email: contact.email ?? '',
  };
}

export type CreateAddressContactDialogResult = {
  // First selected address, kept for callers that still read a single address (back-compat).
  addressId: string;
  address: AddressDto;
  addressIds: string[];
  addresses: AddressDto[];
  contactDto: AddressContactDto | UpdateAddressContactDto;
};

interface CreateAddressContactDialogProps {
  open: boolean;
  onClose: (result?: CreateAddressContactDialogResult) => void;
  contact?: AddressContactDto | null;
  isLoading?: boolean;
  defaultAddress?: AddressDto | null;
  isAddressDisabled?: boolean;
}

export function CreateAddressContactDialog({
  open,
  onClose,
  contact,
  isLoading,
  defaultAddress,
  isAddressDisabled = false,
}: CreateAddressContactDialogProps) {
  const isEditing = !!contact?.id;

  // On edit, load every address the contact is currently linked to (many-to-many) to pre-fill the
  // multi-select. Falls back to the single address carried on the contact if the query is empty.
  const { data: contactAddresses } = useGetAddresses(
    { contactId: contact?.id ?? undefined },
    { query: { enabled: open && isEditing } },
  );

  const {
    control,
    register,
    reset,
    getValues,
    handleSubmit,
    formState: { errors, isDirty: isFormDirty },
  } = useForm<FormValues>({
    defaultValues: EMPTY_FORM_VALUES,
  });

  const [duplicateCheckContact, setDuplicateCheckContact] = useState({ firstName: '', lastName: '' });
  const shouldCheckDuplicate =
    open &&
    !!duplicateCheckContact.firstName &&
    !!duplicateCheckContact.lastName &&
    (!isEditing ||
      duplicateCheckContact.firstName !== contact?.firstName ||
      duplicateCheckContact.lastName !== contact?.lastName);

  const { data: duplicateContactFields } = useCheckAddressContactDuplicate(
    duplicateCheckContact.firstName,
    duplicateCheckContact.lastName,
    shouldCheckDuplicate,
  );

  const firstNameRegistration = register('firstName', { required: true });
  const lastNameRegistration = register('lastName', { required: true });

  const checkDuplicateContact = () => {
    const firstName = getValues('firstName');
    const lastName = getValues('lastName');

    if (isEditing && firstName === contact?.firstName && lastName === contact?.lastName) {
      setDuplicateCheckContact({ firstName: '', lastName: '' });
      return;
    }

    setDuplicateCheckContact({ firstName, lastName });
  };

  useEffect(() => {
    if (!open) return;

    if (contact) {
      const initialAddresses =
        contactAddresses ?? (contact.address ? [contact.address] : []);
      reset(contactToFormValues(contact, initialAddresses));
    } else if (defaultAddress?.id) {
      reset({ ...EMPTY_FORM_VALUES, addresses: [defaultAddress] });
    } else {
      reset(EMPTY_FORM_VALUES);
    }

    setDuplicateCheckContact({ firstName: '', lastName: '' });
  }, [open, contact, contactAddresses, defaultAddress, reset]);

  const handleOnClose = () => {
    reset(EMPTY_FORM_VALUES);
    setDuplicateCheckContact({ firstName: '', lastName: '' });
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={(_, reason) => {
        if (reason === 'backdropClick') return;
        handleOnClose();
      }}>
      <ModalDialog sx={{ width: 800 }}>
        <ModalClose />
        <Typography level="h4">
          {isEditing ? 'Ansprechperson bearbeiten' : 'Ansprechperson erstellen'}
        </Typography>
        <Divider />

        <form
          onSubmit={(event) => {
            event.stopPropagation();

            void handleSubmit((data) => {
              const addressIds = data.addresses
                .map((address) => address.id)
                .filter((id): id is string => !!id);
              if (addressIds.length === 0) return;

              const primaryAddress = data.addresses[0];

              onClose({
                addressId: primaryAddress.id as string,
                address: primaryAddress,
                addressIds,
                addresses: data.addresses,
                contactDto: isEditing
                  ? ({
                      id: contact?.id,
                      addressId: primaryAddress.id,
                      addressIds,
                      salutation: data.salutation || undefined,
                      firstName: data.firstName,
                      lastName: data.lastName,
                      function: data.function || null,
                      phone: data.phone || null,
                      email: data.email || null,
                      isActive: contact?.isActive ?? true,
                      isPinned: false,
                    } satisfies UpdateAddressContactDto)
                  : {
                      addressIds,
                      salutation: data.salutation || undefined,
                      firstName: data.firstName,
                      lastName: data.lastName,
                      function: data.function || null,
                      phone: data.phone || null,
                      email: data.email || null,
                      isActive: true,
                    },
              });
            })(event);
          }}>
          <Stack gap={2}>
            <FormGrid>
              <FormControl error={!!errors.addresses} sx={{ gridColumn: '1 / -1' }}>
                <FormLabel>
                  <Typography>Institutionen *</Typography>
                </FormLabel>

                <Controller
                  name="addresses"
                  control={control}
                  rules={{ validate: (addresses) => addresses.length > 0 }}
                  render={({ field: { onChange, value } }) => (
                    <SelectAddressMulti value={value} onChange={onChange} disabled={isAddressDisabled} />
                  )}
                />

                {errors.addresses && (
                  <FormHelperText>Mindestens eine Institution muss ausgewählt werden</FormHelperText>
                )}
              </FormControl>

              <FormControl>
                <FormLabel>
                  <Typography>Anrede</Typography>
                </FormLabel>
                <Controller
                  name="salutation"
                  control={control}
                  render={({ field }) => (
                    <Select
                      value={field.value}
                      onChange={(_, value) => field.onChange(value ?? '')}
                      placeholder="Bitte auswählen">
                      <Option value="MR">Herr</Option>
                      <Option value="MRS">Frau</Option>
                    </Select>
                  )}
                />
              </FormControl>

              <FormControl>
                <FormLabel>
                  <Typography>Funktion</Typography>
                </FormLabel>
                <Input {...register('function')} />
              </FormControl>

              <FormControl error={!!errors.firstName}>
                <FormLabel>
                  <Typography>Vorname *</Typography>
                </FormLabel>
                <Input
                  {...firstNameRegistration}
                  onBlur={(event) => {
                    void firstNameRegistration.onBlur(event);
                    checkDuplicateContact();
                  }}
                />
                {errors.firstName && <FormHelperText>Vorname muss angegeben werden</FormHelperText>}
                {!errors.firstName && duplicateContactFields?.firstName && (
                  <FormHelperText>Dieser Vorname existiert bereits</FormHelperText>
                )}
              </FormControl>

              <FormControl error={!!errors.lastName}>
                <FormLabel>
                  <Typography>Name *</Typography>
                </FormLabel>
                <Input
                  {...lastNameRegistration}
                  onBlur={(event) => {
                    void lastNameRegistration.onBlur(event);
                    checkDuplicateContact();
                  }}
                />
                {errors.lastName && <FormHelperText>Name muss angegeben werden</FormHelperText>}
                {!errors.lastName && duplicateContactFields?.lastName && (
                  <FormHelperText>Dieser Name existiert bereits</FormHelperText>
                )}
              </FormControl>

              <FormControl error={!!errors.phone}>
                <FormLabel>
                  <Typography>Telefon Nr.</Typography>
                </FormLabel>
                <Input
                  {...register('phone', {
                    validate: (phone) => !phone || isPhoneNumberValid(phone),
                  })}
                  type="tel"
                />
                {errors.phone && <FormHelperText>Diese Telefon Nr. ist nicht korrekt</FormHelperText>}
              </FormControl>

              <FormControl error={!!errors.email}>
                <FormLabel>
                  <Typography>E-Mail</Typography>
                </FormLabel>
                <Input
                  {...register('email', {
                    validate: (email) => !email || isEmail(email),
                  })}
                />
                {errors.email && <FormHelperText>Diese E-Mail-Adresse ist nicht korrekt</FormHelperText>}
              </FormControl>
            </FormGrid>

            <FormActionButtons onAbortClick={handleOnClose} disableSubmit={!isFormDirty} isLoading={isLoading} />
          </Stack>
        </form>
      </ModalDialog>
    </Modal>
  );
}

const FormGrid = styled('div')`
  display: grid;
  gap: 0.75rem 1rem;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  align-items: start;
`;
